import { useCallback, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Creators as GraphqlCreators } from "model/graphql"
import { get } from "lodash"
import { OK, LOADING, DataStatus } from "helpers/data-statuses"
import { POSITIONS_SUBQUERY } from "hooks/rebalancer"
import { transformFormToConfiguration } from "connected-components/optimize-experiments/components/helpers/configuration"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { getDefaultConfiguration } from "connected-components/optimize-experiments/configuration-sections"
import { IsoDate } from "helpers/types/dates"
import {
  OptimizedPositionSet,
  OptimizationConfiguration,
  OptimizationConstraints,
  OptimizationObjective,
} from "connected-components/optimize-experiments/hooks/types"
import { SetupSection, RulesSection } from "connected-components/optimize-experiments/configuration-sections"

const OPTIMIZATION_QUERY = `
query ($modelId: String!, $on: Date!, $positionSet: PositionSetInput!, $base: [PositionSetInput!], $constraints: OptimizationConstraints!, $objectives: [OptimizationObjective]!) {
  model(id: $modelId) {
    optimization(
      positionSet: $positionSet
      base: $base
      on: [$on]
      objective:$objectives
      constraints:$constraints
    ) {
      positions {
        dates {
          ${POSITIONS_SUBQUERY}
        }
      }
    }
  }
}`

interface UseOptimizationOptions {
  overrideConfiguration?: OptimizationConfiguration | null
  setup?: SetupSection | null
  rules?: RulesSection | null
}

interface OptimizationResult {
  id: string
  status: DataStatus
  data?: {
    model?: {
      optimization?: {
        positions?: OptimizedPositionSet
      }
    }
  }
}

interface ReduxState {
  global: {
    selected: {
      account?: string
    }
    data: {
      optimizationResult?: OptimizationResult
    }
  }
}

interface UseOptimizationReturn {
  runOptimization: () => void
  optimizationStatus: DataStatus | null
  optimizedPositionSet: OptimizedPositionSet | null
  getConfiguration: () => OptimizationConfiguration
}

export const useOptimization = (
  instrument: SelectedTypedInstrument | null,
  modelId: string,
  date: IsoDate,
  base: SelectedTypedInstrument | null,
  options: UseOptimizationOptions = {},
): UseOptimizationReturn => {
  const { overrideConfiguration = null, setup = null, rules = null } = options

  const dispatch = useDispatch()
  const account = useSelector((state: ReduxState) => state.global.selected.account)
  const currentOptimizationId = useRef<string | null>(null)
  const optimizationResult = useSelector((state: ReduxState): OptimizationResult | null => {
    if (!currentOptimizationId.current) return null
    const result = state.global.data.optimizationResult
    return result?.id === currentOptimizationId.current ? result : null
  })

  const defaultConfiguration = getDefaultConfiguration()
  const configuration: OptimizationConfiguration = (() => {
    if (overrideConfiguration) {
      return overrideConfiguration
    }
    try {
      return transformFormToConfiguration(
        date,
        setup || defaultConfiguration.setup,
        rules || defaultConfiguration.rules,
      )
    } catch {
      return transformFormToConfiguration(
        date,
        defaultConfiguration.setup,
        defaultConfiguration.rules,
      )
    }
  })()

  const { on, constraints, objectives }: {
    on: IsoDate
    constraints: Partial<OptimizationConstraints>
    objectives: Partial<OptimizationObjective>[]
  } = configuration

  const optimizedPositionSet: OptimizedPositionSet | null =
    optimizationResult && optimizationResult.status === OK
      ? (get(optimizationResult, ["data", "model", "optimization", "positions"]) as OptimizedPositionSet | undefined ?? null)
      : null

  const optimizationStatus: DataStatus | null = optimizationResult?.status ?? null

  const runOptimization = useCallback(() => {
    if (!instrument || !modelId || !date || optimizationStatus === LOADING) return

    const timestamp = Date.now()
    const id = `optimizationResult-${date}-${modelId}-${JSON.stringify(instrument)}-${timestamp}`
    currentOptimizationId.current = id

    dispatch(GraphqlCreators.load(
      "optimizationResult",
      id,
      OPTIMIZATION_QUERY,
      {
        modelId,
        on,
        positionSet: instrument,
        base,
        objectives,
        constraints,
      },
      account,
    ))
  }, [instrument, modelId, date, base, optimizationStatus, dispatch, account, objectives, constraints, on])

  const getConfiguration = useCallback((): OptimizationConfiguration => {
    return {
      on: date,
      objectives,
      constraints,
    }
  }, [date, constraints, objectives])

  return {
    runOptimization,
    optimizationStatus,
    optimizedPositionSet,
    getConfiguration,
  }
}
